/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-unused-vars-experimental */
import React, { useEffect, useRef, useState } from "react";
import { CompetitionData, Fixture, Player } from "./types";
import { useCompetitionData } from "./useCompetitionData";
import { config } from "../config";
import { useAuthContext } from "../context/auth-context";

type ScrapedFixtureResult = {
    date: string;
    homeTeamName: string;
    awayTeamName: string;
    stadiumName: string;
    homeScore: number | null;
    awayScore: number | null;
};

const normalizeTeamName = (teamName: string) => {
    const shortToLong: Record<string, string> = {
        ENG: "england",
        FRA: "france",
        IRE: "ireland",
        ITA: "italy",
        SCO: "scotland",
        WAL: "wales",
    };

    const trimmed = teamName.trim();
    return shortToLong[trimmed.toUpperCase()] ?? trimmed.toLowerCase();
};

const toNullableScore = (score: number | "" | null | undefined): number | null => {
    if (score === "" || score === null || score === undefined) {
        return null;
    }

    return Number.isNaN(score) ? null : score;
};

const emptyFixture: Fixture = {
    id: -1,
    homeTeam: "",
    awayTeam: "",
    homeEmblemUri: "",
    homeEmblemUriText: "",
    awayEmblemUri: "",
    awayEmblemUriText: "",
    homeEmblemUriNegative: "",
    homeEmblemUriNegativeText: "",
    awayEmblemUriNegative: "",
    awayEmblemUriNegativeText: "",
    startTime: "",
    channel: "",
    homeScore: "",
    awayScore: "",
    active: false,
    fullTime: false,
};

const Fixtures = ({
    competitionData,
    fixtures,
    players,
    email,
}: {
    competitionData: CompetitionData;
    fixtures: Fixture[];
    players: Player[];
    email: string;
}) => {
    const [newScores, updateNewScores] = useState<Fixture>(emptyFixture);
    const [localFixtures, updateLocalFixtures] = useState<Fixture[]>(fixtures);
    const {
        tokenStore: { idToken },
    } = useAuthContext();
    const localFixturesRef = useRef<Fixture[]>(fixtures);
    const idTokenRef = useRef<string>(idToken);
    const previousScoresRef = useRef<Record<string, { homeScore: number | null; awayScore: number | null }>>({});

    const { submitScores } = useCompetitionData();

    useEffect(() => {
        updateLocalFixtures(fixtures);
    }, [fixtures]);

    useEffect(() => {
        localFixturesRef.current = localFixtures;
    }, [localFixtures]);

    useEffect(() => {
        idTokenRef.current = idToken;
    }, [idToken]);

    const getResultKey = (result: ScrapedFixtureResult) => `${result.date}|${normalizeTeamName(result.homeTeamName)}|${normalizeTeamName(result.awayTeamName)}`;

    const pollLatestFixtureResults = async () => {
        const fixturesSnapshot = localFixturesRef.current;

        // return early if there are no active matches to avoid unnecessary polling
        if (!fixturesSnapshot.some(fixture => fixture.active && !fixture.fullTime)) {
            return;
        }

        // eslint-disable-next-line no-console
        try {
            const response = await fetch(`${config.apiBaseUrl}/competition/${competitionData.pk}/fixtures/latest`, {
                headers: {
                    Authorization: idTokenRef.current,
                },
            });
            if (!response.ok) {
                return;
            }

            const latestResults = (await response.json()) as ScrapedFixtureResult[];

            // eslint-disable-next-line no-console
            console.log("Updated fixture from poll:", latestResults);

            const changedResults = latestResults.filter(result => {
                const previous = previousScoresRef.current[getResultKey(result)];

                if (previous) {
                    return previous.homeScore !== result.homeScore || previous.awayScore !== result.awayScore;
                }

                // On first poll, compare scraper scores against the fixture scores currently in state.
                const matchingFixture = fixturesSnapshot.find(
                    fixture => normalizeTeamName(fixture.homeTeam) === normalizeTeamName(result.homeTeamName)
                        && normalizeTeamName(fixture.awayTeam) === normalizeTeamName(result.awayTeamName),
                );

                if (!matchingFixture) {
                    return false;
                }

                return (
                    toNullableScore(matchingFixture.homeScore) !== result.homeScore
                    || toNullableScore(matchingFixture.awayScore) !== result.awayScore
                );
            });

            changedResults.forEach(changedResult => {
                const matchingFixture = fixturesSnapshot.find(
                    fixture => normalizeTeamName(fixture.homeTeam) === normalizeTeamName(changedResult.homeTeamName)
                        && normalizeTeamName(fixture.awayTeam) === normalizeTeamName(changedResult.awayTeamName),
                );

                if (matchingFixture) {
                    const updatedFixture: Fixture = {
                        ...matchingFixture,
                        homeScore: changedResult.homeScore ?? 0,
                        awayScore: changedResult.awayScore ?? 0,
                    };

                    // eslint-disable-next-line no-console
                    console.log("Updated fixture from poll:", matchingFixture);

                    submitScores(competitionData.pk, updatedFixture);
                    // eslint-disable-next-line no-restricted-globals
                    // setTimeout(() => location.reload(), 500);
                    return;
                }

                // eslint-disable-next-line no-console
                console.log("Updated fixture from poll:", matchingFixture);
            });

            previousScoresRef.current = latestResults.reduce((acc, result) => {
                acc[getResultKey(result)] = {
                    homeScore: result.homeScore,
                    awayScore: result.awayScore,
                };
                return acc;
            }, {} as Record<string, { homeScore: number | null; awayScore: number | null }>);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };

    useEffect(() => {
        pollLatestFixtureResults();

        const pollingInterval = window.setInterval(() => {
            pollLatestFixtureResults();
        }, 60_000);

        return () => {
            window.clearInterval(pollingInterval);
        };
    }, [competitionData.pk]);

    const handleFormSubmit = async () => {
        const isSubmitted = await submitScores(competitionData.pk, newScores);
        if (!isSubmitted) {
            return;
        }
        updateLocalFixtures(currentFixtures => currentFixtures.map(fixture => {
            if (fixture.id !== newScores.id) {
                return fixture;
            }

            return {
                ...fixture,
                homeScore: newScores.homeScore,
                awayScore: newScores.awayScore,
                active: newScores.active,
                fullTime: newScores.fullTime,
            };
        }));
        updateNewScores(emptyFixture);
    };

    const handleUpdateClick = (fixture: Fixture) => {
        updateNewScores(fixture);
    };

    const getMapping = (breakdown: any) => {
        const breakdownMapping = [
            { breakdown: "exactHome", mapping: "EH" }, // Exact Home
            { breakdown: "exactAway", mapping: "EA" }, // Exact Away
            { breakdown: "winner", mapping: "MW" }, // Winning Team
            { breakdown: "draw", mapping: "MD" }, // Match Draw
            { breakdown: "closestHome", mapping: "CH" }, // Closest Home
            { breakdown: "closestAway", mapping: "CA" }, // Closest Away
            { breakdown: "secondClosestHome", mapping: "2H" }, // Second Closest Home
            { breakdown: "secondClosestAway", mapping: "2A" }, // Second Closest Away
            { breakdown: "thirdClosestHome", mapping: "3H" }, // Third Closest Home
            { breakdown: "thirdClosestAway", mapping: "3A" }, // Third Closest Away
            { breakdown: "home7", mapping: "H7" }, // Home within 7
            { breakdown: "away7", mapping: "A7" }, // Away within 7
        ];
        return breakdown
            ? breakdown.map((bd: string) => breakdownMapping.find(bm => bm.breakdown === bd)?.mapping)
            : [];
    };

    const getBreakdown = (fixtureId: any, predictions: any) => {
        const prediction = predictions.find((p: any) => p.fixtureId === fixtureId);
        return prediction && prediction.scoreBreakdown ? getMapping(prediction.scoreBreakdown) : [];
    };

    return (
        <div className="container text-center p-md-5 mt-5 fixtures-panel">
            <h2 className="pb-3 pt-3">Fixtures</h2>
            <div className="fixtures d-md-flex">
                {(localFixtures as Fixture[]).map(fixture => (
                    <div
                        key={`${fixture.homeTeam}-${fixture.awayTeam}`}
                        className={`col fixture ${
                            fixture.active && "active"
                        } m-0 mb-5 pt-3 pb-3 rounded-3 row flex-column justify-content-between`}
                    >
                        <div className="row">
                            <div className="col-4">
                                <img
                                    alt={fixture.homeEmblemUriNegativeText}
                                    src={fixture.homeEmblemUriNegative}
                                    className="fixtureLogo"
                                />
                            </div>
                            <div className="col-4">
                                <p className="display-5">
                                    {fixture.homeScore} : {fixture.awayScore}
                                </p>
                                <p>{fixture.fullTime ? "FT" : `${fixture.startTime} | ${fixture.channel}`}</p>
                            </div>
                            <div className="col-4">
                                <img
                                    alt={fixture.awayEmblemUriNegativeText}
                                    src={fixture.awayEmblemUriNegative}
                                    className="fixtureLogo"
                                />
                            </div>
                        </div>
                        <div className="row accordion  accordion-flush mt-3" id="accordionExample">
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingOne">
                                    {(fixture.active || fixture.fullTime) && (
                                        <button
                                            className="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#collapse-${fixture.homeTeam}-${fixture.awayTeam}`}
                                            aria-expanded="false"
                                            aria-controls={`collapse-${fixture.homeTeam}-${fixture.awayTeam}`}
                                        >
                                            View Predictions
                                        </button>
                                    )}
                                </h2>
                                <div
                                    id={`collapse-${fixture.homeTeam}-${fixture.awayTeam}`}
                                    className="accordion-collapse collapse"
                                    aria-labelledby="headingOne"
                                    data-bs-parent="#accordionExample"
                                >
                                    <div className="accordion-body">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th> </th>
                                                    <th>{fixture.homeTeam}</th>
                                                    <th> </th>
                                                    <th>{fixture.awayTeam}</th>
                                                    <th> </th>
                                                    <th>Score Breakdown</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(players as Player[])
                                                    .filter(p => p.name !== "TBA")
                                                    .map(player => (
                                                        <tr key={player.email}>
                                                            <td>{player.name}</td>
                                                            <td>
                                                                {player.predictions.find(
                                                                    p => p.fixtureId === fixture.id,
                                                                )
                                                                    && player.predictions.find(
                                                                        p => p.fixtureId === fixture.id,
                                                                    )?.homePrediction}
                                                            </td>
                                                            <td>:</td>
                                                            <td>
                                                                {player.predictions.find(
                                                                    p => p.fixtureId === fixture.id,
                                                                )
                                                                    && player.predictions.find(
                                                                        p => p.fixtureId === fixture.id,
                                                                    )?.awayPrediction}
                                                            </td>
                                                            <td />
                                                            <td>
                                                                {player.predictions.find(
                                                                    p => p.fixtureId === fixture.id,
                                                                )
                                                                    ? getMapping(
                                                                        player.predictions.find(
                                                                            p => p.fixtureId === fixture.id,
                                                                        )?.scoreBreakdown,
                                                                    )?.join(", ")
                                                                    : null}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mt-4 mb-2 d-grid ms-auto flex-grow-1">
                            {email === "gavinepotter@gmail.com" && competitionData.isActive && (
                                <button
                                    type="button"
                                    className="btn btn-success btn-lg shadow-sm score-button"
                                    data-bs-toggle="modal"
                                    data-bs-target={`#score-${fixture.homeTeam}-${fixture.awayTeam}`}
                                    onClick={() => handleUpdateClick(fixture)}
                                >
                                    Update Live Score
                                </button>
                            )}

                            <div
                                className="modal fade"
                                id={`score-${fixture.homeTeam}-${fixture.awayTeam}`}
                                data-bs-backdrop="static"
                                data-bs-keyboard="false"
                                aria-labelledby="staticBackdropLabel"
                                aria-hidden="true"
                            >
                                <div className="modal-dialog">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title" id="staticBackdropLabel">
                                                Add Prediction
                                            </h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                data-bs-dismiss="modal"
                                                aria-label="Close"
                                            />
                                        </div>
                                        <div className="modal-body">
                                            <div className="mb-3 row">
                                                <div className="col-6">
                                                    <div className="input-group">
                                                        <span className="input-group-text text-center">
                                                            {" "}
                                                            <img
                                                                alt={fixture.homeEmblemUriText}
                                                                src={fixture.homeEmblemUri}
                                                                className="formLogo"
                                                            />
                                                        </span>
                                                        <textarea
                                                            className="form-control"
                                                            onChange={({ target: { value } }) => updateNewScores({
                                                                ...newScores,
                                                                id: fixture.id,
                                                                homeScore: Number.isNaN(Number.parseInt(value, 10))
                                                                    ? ""
                                                                    : Number.parseInt(value, 10),
                                                            })}
                                                            value={newScores.homeScore}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="input-group">
                                                        <textarea
                                                            className="form-control"
                                                            onChange={({ target: { value } }) => updateNewScores({
                                                                ...newScores,
                                                                id: fixture.id,
                                                                awayScore: value ? Number.parseInt(value, 10) : 0,
                                                            })}
                                                            value={newScores.awayScore}
                                                        />
                                                        <span className="input-group-text text-center">
                                                            {" "}
                                                            <img
                                                                alt={fixture.awayEmblemUriText}
                                                                src={fixture.awayEmblemUri}
                                                                className="formLogo"
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-6">
                                                    <div className="form-check">
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor="activeCheck"
                                                            style={{ color: "black" }}
                                                        >
                                                            Active
                                                        </label>
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            value=""
                                                            id="activeCheck"
                                                            checked={newScores.active}
                                                            onChange={({ target: { checked } }) => updateNewScores({
                                                                ...newScores,
                                                                id: fixture.id,
                                                                active: checked,
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="form-check">
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor="completedCheck"
                                                            style={{ color: "black" }}
                                                        >
                                                            Completed
                                                        </label>
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            value=""
                                                            id="completedCheck"
                                                            checked={newScores.fullTime}
                                                            onChange={({ target: { checked } }) => updateNewScores({
                                                                ...newScores,
                                                                id: fixture.id,
                                                                fullTime: checked,
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleFormSubmit}
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export { Fixtures };
